import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AuthError } from '@/api/client'
import type { DocumentDetail, FieldKind } from '@/api/docfiller-types'
import {
  queryKeys,
  usePatchSingleField,
} from '@/api/queries'
import { humanizeFieldValue } from './diff'
import {
  AiActionConflictError,
  AiActionPermissionError,
  type AiActionHandlers,
  type AiSuggestionAction,
  type FieldEditAction,
} from './types'

/**
 * Wire an `AiActionTray` to the field PATCH endpoint. The hook
 * encapsulates the apply / reject / force-apply contract the tray expects:
 *  - apply        → conflict-checked PATCH field with `accepted_by="agent"`
 *  - reject       → PATCH field clearing `suggested_value`
 *  - forceApply   → same PATCH as apply, but bypasses the conflict check
 *
 * Conflict detection compares the action's snapshot `before` to the
 * latest cached `current_value` for the same field. If the user (or
 * another agent run) has modified the field since the suggestion was
 * captured, we throw `AiActionConflictError` and the tray surfaces an
 * "Apply anyway" affordance that calls `onForceApply` to commit the
 * suggestion without re-running the same check.
 *
 * 403 responses become `AiActionPermissionError` so the tray can render
 * a permission-denied panel instead of a generic failure.
 */
export function useFieldEditHandlers(
  documentUuid: string,
): Required<Pick<AiActionHandlers, 'onApply' | 'onReject' | 'onForceApply'>> {
  const qc = useQueryClient()
  const singlePatch = usePatchSingleField(documentUuid)
  return useMemo(
    () => {
      const writeSuggestion = async (action: AiSuggestionAction) => {
        if (action.kind !== 'field-edit') {
          throw new Error('useFieldEditHandlers only handles field-edit actions')
        }
        try {
          await singlePatch.mutateAsync({
            fieldId: action.fieldUuid,
            body: {
              current_value: action.after,
              accepted_by: 'agent',
              clear_suggested: true,
            },
          })
        } catch (err) {
          throw mapError(err)
        }
      }

      return {
        onApply: async (action: AiSuggestionAction) => {
          if (action.kind !== 'field-edit') {
            throw new Error(
              'useFieldEditHandlers only handles field-edit actions',
            )
          }
          assertNoConflict(
            action,
            qc.getQueryData<DocumentDetail>(
              queryKeys.documents.detail(documentUuid),
            ),
          )
          await writeSuggestion(action)
        },
        onForceApply: writeSuggestion,
        onReject: async (action: AiSuggestionAction) => {
          if (action.kind !== 'field-edit') return
          try {
            await singlePatch.mutateAsync({
              fieldId: action.fieldUuid,
              body: { clear_suggested: true },
            })
          } catch (err) {
            // Reject is best-effort — if clearing the server-side
            // suggestion fails, we still mark the action as rejected in
            // the tray. But surface auth errors so they don't get hidden.
            if (err instanceof AuthError) throw mapError(err)
            throw err
          }
        },
      }
    },
    [documentUuid, qc, singlePatch],
  )
}

function assertNoConflict(
  action: FieldEditAction,
  cachedDetail: DocumentDetail | undefined,
): void {
  const field = cachedDetail?.fields.find((f) => f.uuid === action.fieldUuid)
  if (!field) return
  const currentNow = field.current_value ?? null
  // The action's `before` snapshot is what we expect the field to still
  // hold. If it doesn't, someone else edited it since we showed the
  // suggestion — force a re-confirm before we clobber their work.
  if (!valuesEquivalent(currentNow, action.before, field.kind as FieldKind | null)) {
    throw new AiActionConflictError(
      currentNow,
      `Value changed since the suggestion was made. Current: ${humanizeFieldValue(
        currentNow,
        field.kind as FieldKind | null,
      ).text}`,
    )
  }
}

function valuesEquivalent(
  a: string | null,
  b: string | null,
  kind: FieldKind | null,
): boolean {
  return humanizeFieldValue(a, kind).text === humanizeFieldValue(b, kind).text
}

export function mapError(err: unknown): Error {
  if (err instanceof AuthError) {
    if (err.status === 403) {
      return new AiActionPermissionError(err.message)
    }
  }
  // openapi-fetch wraps the body in ApiError. AuthError above already
  // covered the 401/403 case; everything else is a generic failure.
  if (err instanceof Error) return err
  return new Error(typeof err === 'string' ? err : 'Apply failed.')
}
